import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { useT } from '@/i18n/context'
import './ClassificationBanner.css'

/** The always-on UTC readout. UTC, not the display zone: a status strip is a
 *  machine fact, and the one clock in the console that is not localised is the
 *  one that says what time it is *for the system*. Data timestamps stay on
 *  Europe/Istanbul; this is deliberately different. */
function useUtcClock(): string {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(id)
  }, [])
  return now.toISOString().slice(11, 19)
}

/**
 * The clock on its own, for when the band is not there.
 *
 * The band is dropped in fullscreen and in the installed window (see
 * `useImmersive`), and of the four things it carries three are letterhead — the
 * caps, the wordmark, the tagline — while this one is a fact. Dropping a strip
 * that repeats the window's own title is tidying; dropping the only clock that
 * reads in UTC is losing something, so it moves rather than goes.
 */
export function UtcClock({ className }: { className?: string }) {
  const clock = useUtcClock()
  return (
    <span className={clsx('k-band__clock', 'k-mono', className)}>
      {clock} <span className="k-band__utc">UTC</span>
    </span>
  )
}

/**
 * The classification band: the thin status strip across the very top of the
 * console. It is a letterhead — hazard-stripe caps, the wordmark, the system
 * tagline and a live UTC clock — that says "this is an instrument, and it is
 * running" before any page has rendered. It carries no controls on purpose.
 *
 * Not drawn when the console has the screen to itself. In an installed window
 * the title bar directly above reads "Kimene — yapay zekâ iş gücü kontrol
 * düzlemi", which is this band's two strings in order; in fullscreen the
 * operator has just asked for the screen. See `useImmersive` for the decision
 * and Shell for where the clock goes instead.
 */
export function ClassificationBanner() {
  const t = useT()
  return (
    <div className="k-band">
      <div className="k-band__side">
        <span className="k-band__caps" aria-hidden="true" />
        <span className="k-band__mark">{t('app.name')}</span>
      </div>
      <span className="k-band__tag">{t('app.tagline')}</span>
      <div className="k-band__side k-band__side--right">
        <UtcClock />
        <span className="k-band__caps" aria-hidden="true" />
      </div>
    </div>
  )
}
